import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { ILogoItem, Logo } from './Logo';
import styles from './logobox.module.scss';
import { PreloaderItem } from './PreloaderItem';

interface ILogoBoxProps {
  className?: string;
  items: ILogoItem[];
  speed?: number;
  isBank?: boolean;
  isPreloader?: boolean;
}

export const LogoBox: React.FC<ILogoBoxProps> = React.memo(({ className, items, speed = 1, isBank, isPreloader }) => {
  const tracksRef = useRef<{ length: number; items: Array<HTMLDivElement | null> }>({ length: 0, items: [] });
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef(0);
  const positionRef = useRef(0);
  const startTimeRef = useRef(0);
  const trackWidthRef = useRef(0);
  const tracksCountRef = useRef(1);
  const [tracksCount, setTrackCount] = useState(1);

  const tracks = Array(tracksCount).fill(null);

  const getTrackWidth = (): number => {
    const track = tracksRef.current.items[0];

    if (track) {
      return track.clientWidth;
    }

    return 0;
  };

  const getTracksCount = (): number => {
    if (!containerRef.current || !trackWidthRef.current) {
      return 1;
    }

    return Math.ceil(containerRef.current.clientWidth / trackWidthRef.current) + 5;
  };

  const setPosition = (offset: number) => {
    const trackWidth = trackWidthRef.current;

    positionRef.current = offset;

    for (let i = 0; i < tracksRef.current.length; i++) {
      const track = tracksRef.current.items[i];

      const trackOffset =
        offset >= trackWidth * (i + 1) ? trackWidth * (tracksCountRef.current - i) - offset : -offset + trackWidth * i;

      if (track) {
        track.style.transform = `translateX(${trackOffset}px)`;
      }
    }
  };

  const animate = (timestamp: number) => {
    try {
      let timePassed = timestamp - startTimeRef.current;

      if (timePassed >= 16) {
        if (timePassed > 50) {
          timePassed = 15;
        }

        const position = (positionRef.current + (timePassed / 40) * speed) % trackWidthRef.current;
        startTimeRef.current = timestamp;

        if (position) {
          setPosition(position);
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    } catch (e) {}
  };

  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const tmp = setInterval(() => {
      const count = getTracksCount();
      trackWidthRef.current = getTrackWidth();

      if (tracksCountRef.current !== count) {
        tracksCountRef.current = count;
        setTrackCount(count);
      }
    }, 500);

    return () => {
      clearInterval(tmp);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);

  if (!Array.isArray(items) || !items.length) {
    return null;
  }

  const halfNumber = items.length / 2;

  return (
    <div className={classNames(className, styles.wrapper)}>
      <div className={styles['track-container']} data-qa="logo-box" ref={containerRef}>
        {tracks.map((_, i) => (
          <div className={styles.track} key={i} style={{ '--half-number': halfNumber }}>
            <div
              className={styles['track-inner']}
              ref={(e) => {
                tracksRef.current.length = tracks.length;
                tracksRef.current.items[i] = e;
              }}
            >
              {items.map((logo, i) =>
                isPreloader ? (
                  <PreloaderItem isSecondLine={i >= halfNumber} />
                ) : (
                  <Logo item={logo} key={i} isBank={isBank} isSecondLine={i >= halfNumber} />
                ),
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});
